import cn from 'classnames';
import { get, writable } from 'svelte/store';

import type { ConfirmDialog } from '@components/common/confirm-dialog';
import { hideTooltip } from '@components/layout/header/components/balance';
import type { IncomeDialog } from '@components/layout/header/components/income-dialog';
import type { BuyMinutesDialog } from '@components/payment/dialog';
import type { SpinnerDialog } from '@components/payment/dialog/spinner-dialog';
import type { RatingOperatorDialog } from '@components/rating/operator-dialog';
import type { RatingUserDialog } from '@components/rating/user-dialog';
import type { GiftChooseDialog } from '@components/room/gift/choose-dialog';
import type { GiftConfirmDialog } from '@components/room/gift/confirm-dialog';
import { ReportReasonsDialog } from '@components/room/reportReasons';
import type { SelfInfoDialog } from '@components/signup/self-info-dialog';
import type { UserAgreementDialog } from '@components/signup/user-agreement-dialog';

import { type DialogProps, type DialogStore, type DialogStoreState, DialogTypes } from './types';

import type { ComponentProps } from 'svelte';

const initialValues: DialogStoreState = {
    isOpened: false,
};

function createStore(): DialogStore {
    const store = writable(initialValues);
    const { subscribe, update } = store;

    const updateStore = (data: Partial<DialogStoreState>) => {
        update((state) => {
            return {
                ...state,
                ...data,
            };
        });
    };

    const closeDialog = () => {
        updateStore({
            isOpened: false,
            props: {},
        });
    };

    const openDialog = (
        dialogType: DialogTypes,
        props?: DialogProps,
        params?: { isHaveCloseBtn?: boolean; closeByOutsideClick?: boolean; closeHandler?: () => void }
    ) => {
        updateStore({
            isOpened: true,
            isHaveCloseBtn: params?.isHaveCloseBtn ?? true,
            closeByOutsideClick: params?.closeByOutsideClick ?? true,
            closeHandler: params?.closeHandler,
            dialogType,
            props: (props ?? {}),
        });
    };

    return {
        store,
        subscribe,
        openDialog,
        showLogin: (closeHandler) => {
            openDialog(DialogTypes.Login, { closeHandler });
        },
        showVpnDetected: () => {
            openDialog(DialogTypes.VpnDetected);
        },
        getState: () => {
            return get(store);
        },
        showSignup: (props, closeHandler, lightBackdrop?: boolean) => {
            openDialog(
                DialogTypes.Signup,
                {
                    ...props,
                    containerClasses: cn('signupDialog', { minified: props?.isMinified }),
                    backdropClasses: lightBackdrop ? 'lightBackdrop' : undefined,
                },
                { closeHandler }
            );
        },
        showRatingOperator: (
            props: ComponentProps<RatingOperatorDialog>,
            closeHandler: () => void
        ) => {
            openDialog(DialogTypes.RatingOperator, props, { closeHandler });
        },
        showRatingUser: (
            props: ComponentProps<RatingUserDialog>,
            closeHandler: () => void
        ) => {
            openDialog(DialogTypes.RatingUser, props, { closeHandler });
        },
        showGiftChooseDialog: (props: ComponentProps<GiftChooseDialog>) => {
            openDialog(DialogTypes.GiftChoose, { ...props, isBottomSheet: true });
        },
        showSpinnerDialog: (props?: ComponentProps<SpinnerDialog>) => {
            openDialog(
                DialogTypes.SpinnerDialog,
                props ?? {},
                { closeByOutsideClick: false, isHaveCloseBtn: false }
            );
        },
        showGiftConfirmDialog: (
            props: ComponentProps<GiftConfirmDialog>,
            closeHandler: () => void
        ) => {
            openDialog(DialogTypes.GiftConfirm, props, { closeHandler });
        },
        showUserAgreement: (props: ComponentProps<UserAgreementDialog>, isHaveCloseBtn = true) => {
            openDialog(DialogTypes.UserAgreement, props, { closeByOutsideClick: false, isHaveCloseBtn });
        },
        showSelfInfo: (props: ComponentProps<SelfInfoDialog>, isHaveCloseBtn = true) => {
            openDialog(DialogTypes.SelfInfo, props, { closeByOutsideClick: false, isHaveCloseBtn });
        },
        showIncome: (props: ComponentProps<IncomeDialog>) => {
            hideTooltip();
            openDialog(DialogTypes.Income, {
                ...props,
            });
        },
        showConfirm: (props: ComponentProps<ConfirmDialog>, closeHandler?: () => void) => {
            openDialog(
                DialogTypes.Confirm,
                closeHandler ? { ...props, onCancel: closeHandler } : props,
                { closeHandler }
            );
        },
        showBuyMembership: (props, closeHandler, lightBackdrop?: boolean) => {
            openDialog(
                DialogTypes.BuyMembership,
                {
                    ...props,
                    containerClasses: 'buyMembershipDialogContent',
                    containerWrapperClasses: 'buyMembershipDialogWrapper',
                    backdropClasses: lightBackdrop ? 'lightBackdrop' : undefined,
                },
                { closeByOutsideClick: false, closeHandler }
            );
        },
        showBuyMinutes: (props: ComponentProps<BuyMinutesDialog>) => {
            openDialog(DialogTypes.BuyMinutes, props, { closeByOutsideClick: false });
        },
        showReportReasons: (props: ComponentProps<ReportReasonsDialog>) => {
            openDialog(DialogTypes.ReportReasons, props);
        },
        showResetPassword: () => {
            openDialog(DialogTypes.ResetPassword);
        },
        showResetPasswordLinkSent: () => {
            openDialog(DialogTypes.ResetPasswordLinkSent);
        },
        showDeactivateAccount: (props) => {
            openDialog(DialogTypes.DeactivateAccount, props);
        },
        showDeleteAccount: (props) => {
            openDialog(DialogTypes.DeleteAccount, props);
        },
        showDisablePackageMinutesAutoRenew: () => {
            openDialog(DialogTypes.DisablePackageMinutesAutoRenew);
        },
        showUserNoteDialog: (props, closeHandler) => {
            openDialog(DialogTypes.UserNote, props, { closeHandler });
        },
        showUnsupportedBrowser: () => {
            openDialog(DialogTypes.UnsupportedBrowser);
        },
        closeDialog,
    };
}

export const dialogStore = createStore();
